import { alpha } from "@mui/system/colorManipulator";

/**
 * createBrandColor — expand a single tenant hex into the full palette colour
 * object the app's theme uses (see `orange` in theme/client/colors.jsx).
 *
 * The tenant `theme` stores one hex per role (brand, brand2, adminPrimary,
 * adminSecondary), but palette consumers read `.main`, `.dark` (hover states),
 * `.contrastText`, and the `.alpha*` variants. Deriving them here lets a tenant
 * colour drop straight into `palette.primary` without every component changing.
 *
 * Returns `null` for a missing/invalid hex so the palette can fall back to its
 * built-in default rather than rendering a broken colour.
 */

const HEX6 = /^[0-9a-f]{6}$/;

/** Normalise `#RGB` / `RRGGBB` / `#RRGGBB` to lowercase `#rrggbb`, else null. */
const normalizeHex = (input) => {
  if (typeof input !== "string") return null;
  let hex = input.trim().replace(/^#/, "").toLowerCase();
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((ch) => ch + ch)
      .join("");
  }
  return HEX6.test(hex) ? `#${hex}` : null;
};

const toChannels = (hex) => [
  parseInt(hex.slice(1, 3), 16),
  parseInt(hex.slice(3, 5), 16),
  parseInt(hex.slice(5, 7), 16),
];

const toHex = (channels) =>
  "#" +
  channels
    .map((c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, "0"))
    .join("");

/** Linearly mix `hex` toward `target` (`0` = unchanged, `1` = fully target). */
const mix = (hex, target, amount) => {
  const a = toChannels(hex);
  const b = toChannels(target);
  return toHex(a.map((ch, i) => ch + (b[i] - ch) * amount));
};

/** White on dark colours, near-black on light ones (YIQ perceived brightness). */
const contrastFor = (hex) => {
  const [r, g, b] = toChannels(hex);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 150 ? "#101828" : "#FFFFFF";
};

/** WCAG relative luminance of a `#rrggbb` colour. */
const luminance = (hex) => {
  const [r, g, b] = toChannels(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** WCAG contrast ratio between two `#rrggbb` colours (1–21). */
export const contrastRatio = (a, b) => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

/** `#rrggbb` → [hue 0–360, saturation 0–1, lightness 0–1]. */
const toHsl = (hex) => {
  const [r, g, b] = toChannels(hex).map((c) => c / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return [0, 0, l];
  const s = d / (1 - Math.abs(2 * l - 1));
  let h;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return [(h * 60 + 360) % 360, s, l];
};

/** [hue, saturation, lightness] → `#rrggbb`. */
const fromHsl = ([h, s, l]) => {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const [r, g, b] =
    h < 60 ? [c, x, 0]
    : h < 120 ? [x, c, 0]
    : h < 180 ? [0, c, x]
    : h < 240 ? [0, x, c]
    : h < 300 ? [x, 0, c]
    : [c, 0, x];
  return toHex([r, g, b].map((v) => (v + m) * 255));
};

/**
 * readableOn — the brand colour with only its lightness raised (on a dark
 * background) or lowered (on a light one) until it reaches `minRatio` contrast
 * against `background`. Hue and saturation are kept, so a navy becomes a
 * clearly-blue mid tone rather than a washed-out near-white. Returns the colour
 * unchanged when it already passes. Use for text/icons drawn in a brand colour;
 * backgrounds that carry white text keep the raw colour.
 */
export const readableOn = (hex, background, minRatio = 4.5) => {
  const main = normalizeHex(hex);
  const bg = normalizeHex(background);
  if (!main || !bg) return hex;
  if (contrastRatio(main, bg) >= minRatio) return main;

  const [h, s, l] = toHsl(main);
  const dir = luminance(bg) < 0.5 ? 1 : -1;
  for (let next = l + dir * 0.02; next > 0 && next < 1; next += dir * 0.02) {
    const candidate = fromHsl([h, s, next]);
    if (contrastRatio(candidate, bg) >= minRatio) return candidate;
  }
  return dir > 0 ? "#ffffff" : "#000000";
};

export const createBrandColor = (input) => {
  const main = normalizeHex(input);
  if (!main) return null;

  return {
    lightest: mix(main, "#ffffff", 0.8),
    light: mix(main, "#ffffff", 0.2),
    main,
    dark: mix(main, "#000000", 0.2),
    darkest: mix(main, "#000000", 0.4),
    contrastText: contrastFor(main),
    alpha4: alpha(main, 0.04),
    alpha8: alpha(main, 0.08),
    alpha12: alpha(main, 0.12),
    alpha30: alpha(main, 0.3),
    alpha50: alpha(main, 0.5),
  };
};
